import React, {useState, useEffect} from 'react';
import styles from './SupplierVerification.module.css';
import globalStyles from '../../styles/globalStyles.module.css';
import forgotPassword from '../../assets/images/forgotPassword.svg';
import {useNavigate} from 'react-router-dom';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {useLocation} from 'react-router-dom';
import {postFetcher} from '../../../api/fetchers';
import {endpoints} from '../../../api/endpoints';
import { useLanguage } from '../../context/LanguageContext';


const SupplierVerification: React.FC = () => {
    const {t} = useLanguage()
    const navigate = useNavigate();
    const location = useLocation();
    const {formData, verificationCode: initialVerificationCode} = location.state || {};
    const [code, setCode] = useState<string[]>(new Array(6).fill(''));
    const [timeLeft, setTimeLeft] = useState<number>(120); // 2 minutes
    const [canResend, setCanResend] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [verificationCode, setVerificationCode] = useState<string>(initialVerificationCode);


    // Handle change in input fields
    const handleChange = (element: HTMLInputElement, index: number) => {
        const value = element.value.replace(/[^0-9]/g, '');
        if (value.length > 1) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Move to the next input field
        if (value && element.nextSibling) {
            (element.nextSibling as HTMLElement).focus();
        }
    };


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const verifyCode = code.join('');

        setIsLoading(true);

        // API call delay
        setTimeout(() => {
            if (verifyCode === verificationCode) {
                navigate('/supplier-information', {state: {formData}});
                toast.success(t('toastMessage', 'rate26'));
            } else {
                toast.error(t('toastMessage', 'rate27'));
                setCode(new Array(6).fill(''));
            }
            setIsLoading(false);
        }, 1000);
    };

    // Resend verification code
    const resendCode = async () => {
        if (canResend) {
            setTimeLeft(120);
            setCanResend(false);

            const newVerificationCode = Math.floor(100000 + Math.random() * 900000).toString();
            setVerificationCode(newVerificationCode);

            try {
                await postFetcher(endpoints.supplier.verificationCode, {
                    arg: {
                        phoneNumber: formData.phone,
                        verificationCode: newVerificationCode
                    }
                });
                toast.info(t('toastMessage', 'rate28'));
            } catch (error) {
                toast.error('Error');
            }
        }
    };

    // Timer effect
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev > 0) return prev - 1;
                clearInterval(timer);
                setCanResend(true);
                return 0;
            });
        }, 1000);

        return () => clearInterval(timer); // Cleanup the timer
    }, []);

    // Format time in mm:ss
    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    return (
        <div className="flex flex-col md:flex-row h-screen w-screen bg-primary-color">
            {/* LEFT SIDE */}
            <div className={`w-full md:w-1/2 flex items-center justify-center ${globalStyles.leftSide} hidden md:flex`}>
                <img src={forgotPassword} alt="Forgot Password Illustration" className="object-cover"/>
            </div>
            {/* RIGHT SIDE */}
            <div
                className={`w-full md:w-1/2 h-full bg-white flex items-center justify-center ${globalStyles.rightSide}`}>
                <div className="w-full max-w-md p-8 space-y-6">
                    {/* VERIFICATION CODE FORM */}
                    <h1 className={`w-full text-4xl text-center text-color-pink  ${globalStyles['poppins-regular']}`}>Doğrulama
                        Kodu</h1>
                    <h6 className="w-full text-center text-gray-500 mb-10">Telefonunuza gelen kodu giriniz.</h6>

                    <form className="space-y-4" onSubmit={handleSubmit}>
                        {/* CODE INPUTS */}
                        <div className="flex justify-between">
                            {code.map((num, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength={1}
                                    value={num}
                                    onChange={(e) => handleChange(e.target, index)}
                                    className={`w-12 h-12 text-center text-2xl border-b-2 border-color-pink placeholder-color-pink focus:outline-none ${styles['code-input']}`}
                                />
                            ))}
                        </div>

                        {/* SUBMIT BUTTON */}
                        <button
                            type="submit"
                            className="w-full px-4 py-2 text-white bg-color-pink font-bold rounded-md hover:bg-hover-color hover:text-color-pink focus:ring-2 focus:ring-pink-500"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Gönderiliyor...' : 'Gönder'}
                        </button>
                    </form>

                    {/* TIMER */}
                    <div className="text-center mt-4">
                        <span className="text-gray-500">
                            {timeLeft > 0 ? `Kodun süresi: ${formatTime(timeLeft)}` : 'Kodun süresi doldu, yeni bir kod istemek için aşağıya tıklayın.'}
                        </span>
                    </div>

                    {/* RESEND LINK */}
                    <div className="text-center mt-4">
                        <span className="text-gray-500">Eğer kod gelmediyse ?</span>
                        <button
                            onClick={resendCode}
                            className={`text-color-pink ml-2 ${!canResend ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!canResend}
                        >
                            Tekrar gönder
                        </button>
                    </div>
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick rtl={false}
                            pauseOnFocusLoss draggable pauseOnHover/>
        </div>
    );
}

export default SupplierVerification;
